import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faSpinner, faMapMarkerAlt } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { CoverageEvaluationApiAdapter } from '../../../adapters/CoverageEvaluationApiAdapter';
import { ZoneOutOfCoverageErrorApiAdapter } from '../../../adapters/ZoneOutOfCoverageErrorApiAdapter';
import type { Order } from '../../../models/Order';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';

export interface ZoneOutOfCoverageErrorModalRef {
  openModal: (orderId: number, order: Order) => void;
  closeModal: () => void;
}

interface ZoneOutOfCoverageErrorModalProps {
  onSuccess?: () => void;
}

export const ZoneOutOfCoverageErrorModal = forwardRef<ZoneOutOfCoverageErrorModalRef, ZoneOutOfCoverageErrorModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
    const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
    const [action, setAction] = useState<'retry' | 'assign'>('retry');
    const [selectedCourierIntegration, setSelectedCourierIntegration] = useState('');
    const [selectedCourierService, setSelectedCourierService] = useState('');
    const [integrations, setIntegrations] = useState<
      Array<{
        label: string;
        value: string;
        courierIntegrationId: string;
        courierServiceId: number;
        courierName: string;
        serviceDescription: string;
      }>
    >([]);
    const [availableServices, setAvailableServices] = useState<Array<{ label: string; value: string }>>([]);
    const [loadingIntegrations, setLoadingIntegrations] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [noIntegrationsAvailable, setNoIntegrationsAvailable] = useState(false);

    useImperativeHandle(ref, () => ({
      openModal: (orderId: number, order: Order) => {
        setCurrentOrderId(orderId);
        setCurrentOrder(order);
        setAction('retry');
        setSelectedCourierIntegration('');
        setSelectedCourierService('');
        setIntegrations([]);
        setAvailableServices([]);
        setNoIntegrationsAvailable(false);
        modalRef.current?.openModal();
      },
      closeModal: () => modalRef.current?.closeModal(),
    }));

    // Cargar integraciones cuando se cambia a modo assign
    useEffect(() => {
      if (action === 'assign' && currentOrder) {
        loadIntegrations();
      }
    }, [action, currentOrder]);

    // Filtrar servicios cuando se selecciona una integración
    useEffect(() => {
      if (selectedCourierIntegration && integrations.length > 0) {
        const selectedIntegration = integrations.find((i) => i.courierIntegrationId === selectedCourierIntegration);
        if (selectedIntegration) {
          setAvailableServices([
            {
              label: selectedIntegration.serviceDescription,
              value: selectedIntegration.courierServiceId.toString(),
            },
          ]);
          // Auto-seleccionar el servicio correspondiente
          setSelectedCourierService(selectedIntegration.courierServiceId.toString());
        } else {
          setAvailableServices([]);
          setSelectedCourierService('');
        }
      } else {
        setAvailableServices([]);
        setSelectedCourierService('');
      }
    }, [selectedCourierIntegration, integrations]);

    const loadIntegrations = async () => {
      if (!currentOrder) return;

      const countryId = currentOrder.country_id;
      const postalCode = currentOrder.customer?.address?.zip || '';
      const paymentMethodId = currentOrder.payment_method_id;

      if (!countryId || !postalCode) {
        toast({
          title: t('zoneOutOfCoverageError.error.title'),
          description: t('zoneOutOfCoverageError.invalidData'),
          variant: 'destructive',
        });
        return;
      }

      setLoadingIntegrations(true);
      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new CoverageEvaluationApiAdapter(httpClient);

        const response = await adapter.evaluateIntegrationsByOrder(countryId, postalCode, paymentMethodId);

        // Filtrar solo las integraciones permitidas
        const allowedIntegrations = response.items.filter((item) => item.allowed);

        if (allowedIntegrations.length === 0) {
          setNoIntegrationsAvailable(true);
          setIntegrations([]);
          return;
        }

        setNoIntegrationsAvailable(false);
        const integrationOptions = allowedIntegrations.map((item) => ({
          label: `${item.courier_integration.name} - ${item.courier.name}`,
          value: `${item.courier_integration_id}-${item.courier_service_id}`,
          courierIntegrationId: item.courier_integration_id,
          courierServiceId: item.courier_service_id,
          courierName: item.courier.name,
          serviceDescription: item.courier_service_description,
        }));

        setIntegrations(integrationOptions);
      } catch (error: any) {
        console.error('Error loading integrations:', error);
        toast({
          title: t('zoneOutOfCoverageError.fetchError.title'),
          description: error?.message || t('zoneOutOfCoverageError.fetchError.description'),
          variant: 'destructive',
        });
      } finally {
        setLoadingIntegrations(false);
      }
    };

    const handleClose = () => {
      modalRef.current?.closeModal();
    };

    const handleBack = () => {
      modalRef.current?.closeModal();
    };

    const handleRetry = async () => {
      if (!currentOrderId) {
        toast({
          title: t('zoneOutOfCoverageError.error.title'),
          description: 'ID del pedido no encontrado',
          variant: 'destructive',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ZoneOutOfCoverageErrorApiAdapter(httpClient);

        const response = await adapter.solveZoneOutOfCoverageError(currentOrderId, {
          action: 'retry',
        });

        if (response.success) {
          toast({
            title: t('zoneOutOfCoverageError.success.title'),
            description: response.message,
            variant: 'default',
          });

          onSuccess?.();
        } else {
          toast({
            title: t('zoneOutOfCoverageError.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        // Traducir el error usando el sistema de traducción
        const translatedError = translateOrderApiError(error, t);

        toast({
          title: t('zoneOutOfCoverageError.error.title'),
          description: translatedError,
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleAssign = async () => {
      if (!currentOrderId) {
        toast({
          title: t('zoneOutOfCoverageError.error.title'),
          description: 'ID del pedido no encontrado',
          variant: 'destructive',
        });
        return;
      }

      if (!selectedCourierIntegration || !selectedCourierService) {
        toast({
          title: t('zoneOutOfCoverageError.error.title'),
          description: t('zoneOutOfCoverageError.validation.required'),
          variant: 'destructive',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ZoneOutOfCoverageErrorApiAdapter(httpClient);

        const selectedIntegration = integrations.find((i) => i.courierIntegrationId === selectedCourierIntegration);

        if (!selectedIntegration) {
          toast({
            title: t('zoneOutOfCoverageError.error.title'),
            description: 'Integración seleccionada no válida',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }

        const response = await adapter.solveZoneOutOfCoverageError(currentOrderId, {
          action: 'assign',
          courier_integration_id: parseInt(selectedIntegration.courierIntegrationId),
          courier_service_id: parseInt(selectedCourierService),
        });

        if (response.success) {
          toast({
            title: t('zoneOutOfCoverageError.success.title'),
            description: response.message,
            variant: 'default',
          });

          onSuccess?.();
        } else {
          toast({
            title: t('zoneOutOfCoverageError.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        // Traducir el error usando el sistema de traducción
        const translatedError = translateOrderApiError(error, t);

        toast({
          title: t('zoneOutOfCoverageError.error.title'),
          description: translatedError,
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('zoneOutOfCoverageError.title')}
        icon={
          <button
            onClick={handleBack}
            className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
          </button>
        }
        type="default"
        fullScreenMobile={true}
        width="lg"
        className="max-h-[90vh]"
      >
        <div className="flex flex-col h-full">
          {/* Contenido principal */}
          <div className="flex-1 space-y-4 mt-2 pb-24 md:pb-0 max-h-[70vh] overflow-y-auto">
            <p className="text-sm">{t('zoneOutOfCoverageError.description')}</p>

            {/* Selector de acción */}
            <div className="space-y-3">
              <div className="flex gap-3">
                <button
                  onClick={() => setAction('retry')}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    action === 'retry'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-hover-press'
                  }`}
                >
                  <div className="font-medium">{t('zoneOutOfCoverageError.retry.title')}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('zoneOutOfCoverageError.retry.description')}
                  </div>
                </button>
                <button
                  onClick={() => setAction('assign')}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    action === 'assign'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-hover-press'
                  }`}
                >
                  <div className="font-medium">{t('zoneOutOfCoverageError.assign.title')}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('zoneOutOfCoverageError.assign.description')}
                  </div>
                </button>
              </div>

              {/* Opción Assign */}
              {action === 'assign' && (
                <div className="space-y-4 p-4 border border-border rounded-lg bg-background">
                  {loadingIntegrations ? (
                    <div className="flex items-center justify-center py-8">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted-foreground w-6 h-6" />
                      <span className="ml-3 text-sm text-muted-foreground">
                        {t('zoneOutOfCoverageError.loadingIntegrations')}
                      </span>
                    </div>
                  ) : noIntegrationsAvailable ? (
                    <div className="py-8 px-4 text-center">
                      <div className="mb-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="w-6 h-6 text-muted-foreground" />
                        </div>
                      </div>
                      <p className="text-sm font-medium text-foreground mb-2">
                        {t('zoneOutOfCoverageError.noIntegrations.message')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('zoneOutOfCoverageError.noIntegrations.contactSupport')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <label className="text-title font-medium">
                          {t('zoneOutOfCoverageError.assign.courierIntegration')}
                        </label>
                        <Combobox
                          options={integrations.map((i) => ({
                            label: i.label,
                            value: i.courierIntegrationId,
                          }))}
                          value={selectedCourierIntegration}
                          setValue={setSelectedCourierIntegration}
                          placeholder={t('zoneOutOfCoverageError.assign.selectCourierIntegration')}
                          disabled={loadingIntegrations || integrations.length === 0}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-title font-medium">
                          {t('zoneOutOfCoverageError.assign.courierService')}
                        </label>
                        <Combobox
                          options={availableServices}
                          value={selectedCourierService}
                          setValue={setSelectedCourierService}
                          placeholder={t('zoneOutOfCoverageError.assign.selectCourierService')}
                          disabled={
                            !selectedCourierIntegration || loadingIntegrations || availableServices.length === 0
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Botones de acción - Fijos en mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose} size="lg">
                {t('zoneOutOfCoverageError.cancel')}
              </Button>
              {action === 'retry' ? (
                <Button variant="primary" onClick={handleRetry} disabled={isSubmitting} size="lg">
                  {isSubmitting ? t('zoneOutOfCoverageError.submitting') : t('zoneOutOfCoverageError.retry.button')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleAssign}
                  disabled={
                    !selectedCourierIntegration ||
                    !selectedCourierService ||
                    isSubmitting ||
                    loadingIntegrations ||
                    noIntegrationsAvailable
                  }
                  size="lg"
                >
                  {isSubmitting ? t('zoneOutOfCoverageError.submitting') : t('zoneOutOfCoverageError.assign.button')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);
